import axios, {AxiosResponse} from "axios"
import router from "@/router";
import store from "@/store";

const axiosApi = axios.create({
    baseURL: `/api/private`,
    headers: { "Content-Type": "application/json" }
});

const axiosImport = axios.create({
    baseURL: `/api/private`,
    headers: { "Content-Type": "multipart/form-data" }
});

const axiosExport = axios.create({
    baseURL: `/api/private`,
    headers: { "Content-Type": "application/json" },
    responseType: "arraybuffer"
});

// Resolves a message for errors that never reached the server (network down, request
// aborted, timeout) or that came back without a JSON body. Without this the catch blocks
// below dereference `error.response.data` and throw, leaving the caller's promise pending
// forever - which keeps loading flags (e.g. isImporting) stuck on.
function resolveTransportError(error: any): string | null {
    if (error == null) {
        return "Unexpected error. Please try again.";
    }
    if (error.response == null) {
        if (error.code === "ECONNABORTED" || /timeout/i.test(error.message || "")) {
            return "The request timed out. The file may be too large or the server is busy. Please try again.";
        }
        return "Could not reach the server. Please check your connection and try again.";
    }
    return null;
}

export default {
    axiosPost(url: string, request: any): Promise<AxiosResponse<any>> {
		return new Promise((resolve, reject) => {
			axiosApi.post(url, request)
			.then(function (response) {
				resolve(response.data);
				store.dispatch("startLogoutAlertCountdown");
			})
			.catch(function (error) {
				const transportError = resolveTransportError(error);
				if (transportError != null) {
					reject(transportError);
					return;
				}
				switch(error.response.status) {
				case 401:
					store.dispatch("clearSession");
					router.push("/login");
					break;
				}
				reject((error.response.data && error.response.data.message) ? error.response.data.message : "The server returned an error (HTTP " + error.response.status + ").");
			});
		});
    },
    axiosPostImport(url: string, formData: any): Promise<AxiosResponse<any>> {
		return new Promise((resolve, reject) => {
			axiosImport.post(url, formData)
			.then(function (response) {
				resolve(response.data);
			})
			.catch(function (error) {
				const transportError = resolveTransportError(error);
				if (transportError != null) {
					reject(transportError);
					return;
				}
				switch(error.response.status) {
				case 401:
					store.dispatch("clearSession");
					router.push("/login");
					reject("Your session has expired. Please sign in again and retry the import.");
					return;
				case 403:
					reject("You are not allowed to import this report.");
					return;
				case 413:
					reject("The file is too large to upload.");
					return;
				}
				reject((error.response.data && error.response.data.message) ? error.response.data.message : "The server returned an error (HTTP " + error.response.status + ").");
			});
		});
    },
    axiosPostExport(url: string, request: any): Promise<AxiosResponse<any>> {
		return new Promise((resolve, reject) => {
			axiosExport.post(url, request)
			.then(function (response) {
				// axios >= 0.28 types response headers as `string | undefined` (0.21 typed them as
				// `any`), so the header has to be checked before it is parsed. Fall back to a generic
				// name when the server did not send a Content-Disposition rather than failing the
				// download.
				const headerLine = response.headers["content-disposition"] as string | undefined;
				let filename = "download";
				if (headerLine != null) {
					const startFilenameIndex = headerLine.indexOf('"') + 1;
					const endFilenameIndex = headerLine.lastIndexOf('"');
					if (endFilenameIndex > startFilenameIndex) {
						filename = headerLine.substring(startFilenameIndex, endFilenameIndex);
					}
				}

				const url = window.URL.createObjectURL(new Blob([response.data], {type: response.headers["content-type"] as string | undefined}));
				const link = document.createElement("a");
				link.href = url;
				link.setAttribute("download", filename); //or any other extension
				document.body.appendChild(link);
				link.click();
				
				resolve(response.data);
			})
			.catch(function (error) {
				const transportError = resolveTransportError(error);
				if (transportError != null) {
					reject(transportError);
					return;
				}
				if (error.response.status === 401) {
					store.dispatch("clearSession");
					router.push("/login");
					reject("Your session has expired. Please sign in again.");
					return;
				}
				// The response body is an ArrayBuffer (responseType is set for the file download),
				// so decode it defensively - an HTML error page would break JSON.parse and leave
				// this promise pending.
				let message = "The server returned an error (HTTP " + error.response.status + ").";
				try {
					message = JSON.parse(new TextDecoder("utf-8").decode(error.response.data)).message || message;
				} catch (parseError) {
					// keep the generic message
				}
				reject(message);
			});
		});
    }
}
